import { EChartsOption } from 'echarts';
import { PieChartConfig, EnhancedPieChartConfig } from '../models/chart-types';
import {
  getTitleConfig,
  CHART_COLORS,
  CHART_TYPOGRAPHY,
  CHART_DEFAULTS,
} from '../constants/chart-theme.constants';

/**
 * Build ECharts options for pie chart
 */
export function buildPieChartOptions(config: PieChartConfig | EnhancedPieChartConfig): EChartsOption {
  const enhancedConfig = config as EnhancedPieChartConfig;
  const isDonut = enhancedConfig.variant === 'styled-v1' || enhancedConfig.variant === 'styled-v2';
  
  // Determine radius based on size configuration
  const getRadius = (): [string, string] | string => {
    // If custom radius is provided, use it
    if (enhancedConfig.customRadius) {
      return enhancedConfig.customRadius;
    }
    
    const size = enhancedConfig.size || 'medium';
    
    // Size presets for donut charts
    if (isDonut) {
      if (size === 'small') return ['35%', '55%'];
      if (size === 'large') return ['50%', '80%'];
      return ['45%', '70%']; // medium (default)
    }
    
    // Size presets for normal pie charts
    if (size === 'small') return '40%';
    if (size === 'large') return '70%';
    return '55%'; // medium (default)
  };
  
  // Define gradient colors for styled variants
  const getGradientColors = (index: number) => {
    const gradients = [
      // Cyan-blue gradient (for first item - Flagged)
      {
        type: 'linear' as const,
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: '#22d3ee' }, // cyan-400
          { offset: 1, color: '#0891b2' }, // cyan-600
        ],
      },
      // Orange gradient (for second item - Not flagged)
      {
        type: 'linear' as const,
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: '#fb923c' }, // orange-400
          { offset: 1, color: '#ea580c' }, // orange-600
        ],
      },
      // Emerald gradient (for third item)
      {
        type: 'linear' as const,
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          { offset: 0, color: '#34d399' }, // emerald-400
          { offset: 1, color: '#059669' }, // emerald-600
        ],
      },
    ];
    return gradients[index % gradients.length];
  };

  const options: EChartsOption = {
    title: getTitleConfig(config.title, config.subtitle),

    // Remove all padding/margins for styled variants
    grid: isDonut ? {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      containLabel: false
    } : undefined,

    tooltip: {
      trigger: config.tooltip?.trigger || 'item',
      formatter: config.tooltip?.formatter || '{a} <br/>{b}: {c} ({d}%)',
      ...config.tooltip,
    },

    legend: config.legend ? {
      show: config.legend.show !== false,
      orient: config.legend.orient || 'vertical',
      left: config.legend.left || 'left',
      top: config.legend.top || 80,
      icon: 'circle',
      itemWidth: CHART_DEFAULTS.legend.iconSize,
      itemHeight: CHART_DEFAULTS.legend.iconSize,
      itemGap: CHART_DEFAULTS.legend.itemGap,
      textStyle: {
        color: CHART_COLORS.text.tertiary,
        fontSize: CHART_TYPOGRAPHY.legend.fontSize,
      },
      borderWidth: 0,
      ...config.legend,
    } as any : undefined,

    series: config.series.map((series) => ({
      name: series.name || 'Data',
      type: 'pie',
      data: isDonut 
        ? series.data.map((item, index) => ({
            ...item,
            itemStyle: {
              color: getGradientColors(index),
            },
          }))
        : series.data,
      radius: series.radius || getRadius(),
      center: series.center || ['50%', '50%'],
      roseType: series.roseType,
      label: series.label !== undefined ? series.label : (isDonut ? { show: false } : {
        show: true,
        formatter: '{b}: {d}%',
        color: CHART_COLORS.text.primary,
        fontSize: CHART_TYPOGRAPHY.label.fontSize,
        fontWeight: CHART_TYPOGRAPHY.label.fontWeight,
        borderWidth: 0,
        backgroundColor: 'transparent',
        padding: 0,
      }),
      labelLine: {
        show: true,
        length: 15,
        length2: 10,
        lineStyle: {
          color: CHART_COLORS.structure.labelLine,
          width: 1,
        },
      },
      emphasis: series.emphasis || {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
        },
      },
    })),
  };

  // Merge with custom options if provided
  return config.customOptions 
    ? { ...options, ...config.customOptions }
    : options;
}

